import React, { useState, useEffect, useMemo, useContext } from 'react'
import { ThemeProvider } from "@pixel/core";
import { createInternalTheme } from "@pixel/internal/styles";
import Typography from '@pixel/core/Typography';
import Paper from "@pixel/core/Paper";
import DataTable from '@pixel/core/DataTable'
import Link from '@pixel/core/Link'
import Tooltip from "@pixel/core/Tooltip";
import ListBase, { 
    ListAppliedSearchFilterProps,
    ListSearchChangeProps
} from '@pixel/core/ListBase';
import { SelectChangeEvent } from '@mui/material/Select';
import CircularProgress from "@pixel/core/CircularProgress";
import PageHeader from "@pixel/core/PageHeader";
import LayoutRoot from "@pixel/internal/LayoutRoot";
import LayoutRootSection from "@pixel/internal/LayoutRootSection";
import TableChartIcon from '@pixel/assets/icons/TableChart';
import Breadcrumb from "@pixel/core/Breadcrumb";
import BreadcrumbItem from "@pixel/core/BreadcrumbItem";
import ActionGroup from '@pixel/core/ActionGroup';
import OdsContext from '../OdsContext';
import { useMultipleLocalizedText } from "../../../contexts/Localization/localizationHooks";
import { IBusinessResultData, IBusinessData, IBusinessGetParms, IBUSearchGetParms, IBreadcrumbHierarchyObject } from "../OdsTypes";
import Button from '@pixel/core/Button';
import Box from '@pixel/core/Box';
import axios from "axios";
import { transformBUResultData, isAvailableInBreadcrumb, updateBreadcrumb } from "../OdsTransform";
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import AddBusinessUnit from './AddBusinessUnit';
import { useSnackbar } from '@pixel/core/SnackbarService';

const BusinessUnit = () => {
    const jvBusinessTitle = {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        wordBreak: 'break-all',
        whiteSpace: 'nowrap',
        display: 'inline-block',
    }

    const overlayStyling = {
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        alignItems: 'center',
        zIndex: '1',
    }

    const confirm = useConfirmation();
    const OrgElementsContext = useContext(OdsContext);
    const {
        ODS_TAB_BUSINESS_UNITS,
        ODS_ALL,
        ODS_SEARCH_PLACEHOLDER,
        ODS_SEARCH_BUSINESS_UNITS,
        ODS_SUB_BUSINESS_UNITS,
        ODS_DESCRIPTION,
        ODS_STATUS,
        ODS_ADD_BUSINESS_BUTTON,
        ODS_ROWS_PER_PAGE_LABEL,
        ODS_ACTIONS_ADD_BU,
        ODS_ACTIONS_DUPLICATE,
        ODS_ACTIONS_EDIT,
        ODS_ACTIONS_MAKE_ACTIVE,
        ODS_ACTIONS_MAKE_INACTIVE,
        I18N_NO_RESULTS_FOUND,
        ODS_PARENT_BUSINESS_UNIT,
        ODS_INACTIVATE_BU_WITH_SUBSIDIARIES_CONFIRMATION_DESC,
        ODS_MAKE_BU_INACTIVATE_SUCCESSFUL,
        ODS_UPDATE_BU_MODAL_TITLE,
        ODS_ACTIVATE_BU_WITH_SUBSIDIARIES_CONFIRMATION_DESC,
        ODS_MAKE_BU_ACTIVATE_SUCCESSFUL,
        ODS_MAKE_BU_WITH_SUBSIDIARIES_INACTIVATE_SUCCESSFUL,
        ODS_BU_UPDATE_FAILED,
      } = useMultipleLocalizedText()

      const ODS_DIMENSIONS = {
        ODS_DIMENSION_PARENT_IS_INACTIVE :'ODS_DIMENSION_PARENT_IS_INACTIVE',
        ODS_DIMENSION_HAS_REQUISITIONS : 'ODS_DIMENSION_HAS_REQUISITIONS',
      }

    const ODSPageSizeSessionStorage = () => JSON.parse(sessionStorage.getItem("ODS_PageSize")) ? JSON.parse(sessionStorage.getItem("ODS_PageSize")) : OrgElementsContext.defaultPageSize;

    const BUFetchParams: IBusinessGetParms = useMemo(() => ({
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
    }), []);

    const BUSearchParams: IBUSearchGetParms = useMemo(() => ({
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
        keywords: '',
    }), []);

    const snack = useSnackbar();
    const [BUFetchPayload, setBUFetchPayload] = useState<IBusinessGetParms>(BUFetchParams);
    const [BUSearchPayload, setBUSearchPayload] = useState<IBUSearchGetParms>(BUSearchParams);
    const [openAddBusinessDrawer, setOpenAddBusinessDrawer] = useState(false);
    const [results, setResults] = useState<IBusinessResultData>({ nodes: [], totalCount: 0, pageNumber: 0, pageSize: ODSPageSizeSessionStorage()});
    const [BUHierarchyTree, setBUHierarchyTree] = useState([{ name: 'All', eid: '' }]);
    const [perPageSize, setPerPageSize] = useState(ODSPageSizeSessionStorage());
    const [listLoading, setListLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [duplicateSelectedBU, setDuplicateSelectedBU] = useState(null);
    const [selectedBU, setSelectedBU] = useState(null);
    const [noSearchResult, setNoSearchResult] = useState(false);
    const [editSelectedBU, setEditSelectedBU] = useState(null);
    const [showInProgress, setShowInProgress] = useState(false);
    const [pagingContext, setPagingContext] = useState({
        hideFirstLastActions: true,
        perPage: perPageSize,
        page: 1,
        first: 1,
        last: perPageSize,
      });

    const fetchBusinessUnitList = async (BUFetchPayload: IBusinessGetParms) => {
            try {
                setListLoading(true);
                await axios.get(window.jvUrls.admin + '/action/ods/businessUnits', {
                    params: BUFetchPayload
                })
                    .then(function (response) {
                        setResults(transformBUResultData(response.data));
                        if(BUHierarchyTree.length === 1){
                            sessionStorage.setItem("BU_company", JSON.stringify(response.data.businessUnits[0])); 
                        }
                        response.data.businessUnits.length > 0 && setNoSearchResult(false);
                        setPagingContext({
                            hideFirstLastActions: true,
                            perPage: response.data.pageSize,
                            page: response.data.pageNumber + 1,
                            first: response.data.businessUnits.length > 0 ? response.data.pageNumber * response.data.pageSize + 1 : 0,
                            last: response.data.pageSize * response.data.pageNumber + response.data.businessUnits.length,
                          });
                          setListLoading(false);
                    })
                    .catch(function (error) {
                        console.log(error);
                        setListLoading(false);
                    });
            } catch (error) {
                console.log(error);
            }
        }

        const setBUListOnActions = () => {
            if(searchText !== '') {
                setBUHierarchyTree([{ name: 'All', eid: '' }]);
                setBUFetchPayload({pageSize: ODSPageSizeSessionStorage(), pageNumber: 0});
            }
        }

        useEffect(() => {
            fetchBusinessUnitList(BUFetchPayload);
            if(!openAddBusinessDrawer){
                setSelectedBU(null);
                setEditSelectedBU(null);
            } else {
                setSearchText('');
            }
        }, [BUFetchPayload, openAddBusinessDrawer]);

        const updatePayload = (business: IBreadcrumbHierarchyObject) => {
            if (business.eid === '') {
                  setBUFetchPayload({pageSize: ODSPageSizeSessionStorage(), pageNumber: 0});
            } else {
                  setBUFetchPayload({...BUFetchPayload, parentEId: business.eid, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0});
            }
        }

    const onBreadcrumChange = (business: IBreadcrumbHierarchyObject) => {
        setBUHierarchyTree(updateBreadcrumb(BUHierarchyTree, business));
        updatePayload(business);
    }

    const handleAddBusinessDrawerOpen = () => {
        setOpenAddBusinessDrawer(true);
    };

    const duplicateBusinessUnit = (node: IBusinessData) => {
        editBusinessUnit(node);
        setDuplicateSelectedBU(true);
    };

    const buildSuccessSnackbarResponse = (node: IBusinessData, status: boolean): String => {
        let response = '';
        if (status) {
            response = ODS_MAKE_BU_ACTIVATE_SUCCESSFUL;
          } else {
            if (node.subBusinessUnitsCount > 0) {
                response = ODS_MAKE_BU_WITH_SUBSIDIARIES_INACTIVATE_SUCCESSFUL;
              } else {
                  response = ODS_MAKE_BU_INACTIVATE_SUCCESSFUL;
              }
          }
          return response;
    }

    const toggleBUStatus = (node: IBusinessData, status: boolean) => {
        try {
            axios.put(
                `${window.jvUrls.admin}/action/ods/businessUnits/activation/${node.eid}`,
                { active: status },
                {
                    headers: { 'Content-Type': 'application/json' }
                })
                .then(response => {
                    setOpenAddBusinessDrawer(false);
                    const snackbarResponse = buildSuccessSnackbarResponse(node, status);
                    snack.showSuccess(`${node.name} ${snackbarResponse}`, OrgElementsContext.snackBarAutoHideTime);
                    searchText === '' && updatePayload(BUHierarchyTree[BUHierarchyTree.length - 1]);
                    searchText !== '' && setSearchText('');
                    setShowInProgress(false);
                    fetchBusinessUnitList(BUFetchPayload);
                }).catch(error => {
                    setShowInProgress(false);
                    if (error.response.status === 400) {
                        const { errorCode, message } = error.response.data;
                        if (errorCode === ODS_DIMENSIONS.ODS_DIMENSION_HAS_REQUISITIONS) {
                            snack.showError(message, OrgElementsContext.snackBarAutoHideTime);
                        }
                        if (errorCode === ODS_DIMENSIONS.ODS_DIMENSION_PARENT_IS_INACTIVE) {
                            confirm.open({
                                dialogLabelSet: DialogLabelSet.ALERT,
                                title: ODS_UPDATE_BU_MODAL_TITLE,
                                description: message,
                                onResolve: (reason: string) => { },
                                onReject: (reason: string) => { }
                            });
                        }
                    } else {
                        snack.showError(`${ODS_BU_UPDATE_FAILED}`, OrgElementsContext.snackBarAutoHideTime);
                    }
                    searchText !== '' && setSearchText('');
                })
        } catch (error) {
            snack.showError(`${ODS_BU_UPDATE_FAILED}`, OrgElementsContext.snackBarAutoHideTime);
        }
    }

    const makeBusinessUnitActive = (node: IBusinessData) => {
        setOpenAddBusinessDrawer(false);
        node == null && (node = editSelectedBU);
        if (node.subBusinessUnitsCount > 0) {
            confirm.open({
                dialogLabelSet: DialogLabelSet.CONFIRM,
                title: ODS_UPDATE_BU_MODAL_TITLE,
                description: ODS_ACTIVATE_BU_WITH_SUBSIDIARIES_CONFIRMATION_DESC,
                onResolve: (reason: string) => {
                    setShowInProgress(true);
                    toggleBUStatus(node, true);
                    searchText !== '' && setSearchText('');
                },
                onReject: (reason: string) => {
                    setSearchText('');
                }
            });
        } else {
            setShowInProgress(true);
            toggleBUStatus(node, true);
        }
    }

    const makeBusinessUnitInActive = (node: IBusinessData) => {
        setOpenAddBusinessDrawer(false);
        node == null && (node = editSelectedBU);
        if (node.subBusinessUnitsCount > 0) {
            confirm.open({
                dialogLabelSet: DialogLabelSet.CONFIRM,
                title: ODS_UPDATE_BU_MODAL_TITLE,
                description: ODS_INACTIVATE_BU_WITH_SUBSIDIARIES_CONFIRMATION_DESC,
                onResolve: (reason: string) => {
                    setShowInProgress(true);
                    toggleBUStatus(node, false);
                    searchText !== '' && setSearchText('');
                },
                onReject: (reason: string) => {
                    setSearchText('');
                }
            });
        } else {
            setShowInProgress(true);
            toggleBUStatus(node, false);
        }
    }

    const AddBusinessUnitAction = (node: IBusinessData) => {
        setSelectedBU(node);
        setOpenAddBusinessDrawer(true);
    }

    const editBusinessUnit = (node: IBusinessData) => {
      try {
        setListLoading(true);
        setSelectedBU(node);
        axios
          .get(window.jvUrls.admin + "/action/ods/businessUnits/" + node.eid)
          .then(function (response) {
            setSelectedBU({...response?.data, subBusinessUnitsCount: node.subBusinessUnitsCount});
            setEditSelectedBU(response.data);
            setOpenAddBusinessDrawer(true);
          })
          .catch(function (error) {
            console.log(error);
          });
      } catch (error) {
        console.log(error);
      }
    };

    const transformActionList = (actionList: any) => {
        const BUActions = [
            { i18Label: ODS_ACTIONS_ADD_BU, name: "addSubBu" },
            { i18Label: ODS_ACTIONS_DUPLICATE, name: "duplicate" },
            { i18Label: ODS_ACTIONS_EDIT, name: "edit" },
            { i18Label: ODS_ACTIONS_MAKE_ACTIVE, name: "makeActive" },
            { i18Label: ODS_ACTIONS_MAKE_INACTIVE, name: "makeInactive" },
        ];
        const actionLinks: any = [];
        if(actionList){
            Object.keys(actionList).forEach((key) => {
                let filteredAction = BUActions.filter((e) => e.name === key);
                if (filteredAction.length > 0 && actionList[key] !== 'hidden') {
                    let actionTertiaryLink = {
                        type: "tertiary",
                        label: filteredAction[0].i18Label,
                        disabled: actionList[key] === 'grayout',
                        onClick: (event: any, data: any) => {
                            setBUListOnActions();
                            filteredAction[0].name === 'duplicate' && duplicateBusinessUnit(data);
                            filteredAction[0].name === 'addSubBu' && AddBusinessUnitAction(data);
                            filteredAction[0].name === 'edit' && editBusinessUnit(data);
                            filteredAction[0].name === 'makeActive' && makeBusinessUnitActive(data);
                            filteredAction[0].name === 'makeInactive' && makeBusinessUnitInActive(data);
                        }
    
                    };
                    actionLinks.push(actionTertiaryLink);
                }
            });
        }
        return actionLinks;
    };

    const onHandleBUClick = (business: IBreadcrumbHierarchyObject) => {
        const newEntry = { name: business.name, eid: business.eid };
        setSearchText('');
        !isAvailableInBreadcrumb(BUHierarchyTree, business) && setBUHierarchyTree((BUHierarchyTree) => [...BUHierarchyTree, newEntry]);
        setBUFetchPayload({...BUFetchPayload, parentEId: business.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage()});
    }

    const onPageChanged = (event: any, currPage: number) => {
        if(searchText.length > 1){
            setBUSearchPayload({...BUSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1});
        } else {
            if (BUHierarchyTree[BUHierarchyTree.length - 1].eid === '') {
                setBUFetchPayload({pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1});
            } else {
                setBUFetchPayload({...BUFetchPayload, pageSize: ODSPageSizeSessionStorage(), parentEId: BUHierarchyTree[BUHierarchyTree.length - 1].eid, pageNumber: currPage - 1});
            }
        }
    }

    const onPaginationChange = (event: SelectChangeEvent) => {
        sessionStorage.setItem("ODS_PageSize", JSON.stringify(Number(event.target.value)));
        setPerPageSize(Number(event.target.value));
        if (searchText !== ''){
            setBUSearchPayload({...BUSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(searchText)})
            setListLoading(true);
        } else {
            updatePayload(BUHierarchyTree[BUHierarchyTree.length - 1]);
        }
    }

    const onHandleSearchedBUClick = (business: IBreadcrumbHierarchyObject, BUTree: Array<IBreadcrumbHierarchyObject>) => {
        let parentCompany = JSON.parse(sessionStorage.getItem("BU_company"));
        parentCompany.eid === business.eid && BUTree.length == 2 &&  BUTree.pop();
        const newEntry = [{ name: 'All', eid: '' }, ...BUTree];
        setSearchText('');
        setBUHierarchyTree(newEntry);
        setBUFetchPayload({...BUFetchPayload, parentEId: business.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage()});
    }

    const renderParentBu = (node: IBusinessData) => {
        const list: any = [];
        if (node.ancestors && node.ancestors.length > 0) {
              node.ancestors.map((parent, index) => {
                list.push(
                    <span style={{display:'inline-flex'}}>
                        <Tooltip key={parent.eid} title={parent.name} arrow placement="bottom"><Link underline="hover" sx={{ ...jvBusinessTitle, fontWeight: '600', maxWidth: '140px' }} onClick={() => onHandleSearchedBUClick({name: parent.name, eid: parent.eid}, node.ancestors)} variant="body2">{parent.name}</Link></Tooltip>
                        <span style={{padding:'0 6px'}}>{index < node.ancestors.length - 1 && ' /   '}</span>
                    </span>
                );
              })
            }
        return list;
    }

    const populateTitle = (node: IBusinessData) => {
        if (node.subBusinessUnitsCount > 0) {
            return <Tooltip title={node.name} arrow placement="bottom"><Link underline="hover" sx={{ ...jvBusinessTitle, maxWidth: '100%', fontWeight: '600' }} onClick={() => (node.ancestors && searchText.length > 1) ? onHandleSearchedBUClick({name: node.name, eid: node.eid}, [...node.ancestors, {name: node.name, eid: node.eid}]) : onHandleBUClick(node)} variant="body2">{node.name}</Link></Tooltip>;
        } else {
            return <Tooltip title={node.name} arrow placement="bottom"><span><Typography variant="body2" sx={{...jvBusinessTitle, maxWidth: '100%'}} disabled={!node.active}>{node.name}</Typography></span></Tooltip>;
        }
    }

    useEffect(() => {
        if(searchText && searchText.length > 1) {
            axios.get(window.jvUrls.admin + '/action/ods/businessUnits/search', {
                params: BUSearchPayload
            }) 
                .then(function (response) {
                    setResults(transformBUResultData(response.data));
                    setPagingContext({
                        hideFirstLastActions: true,
                        perPage: response.data.pageSize,
                        page: response.data.pageNumber + 1,
                        first: response.data.businessUnits.length > 0 ? response.data.pageNumber * response.data.pageSize + 1 : 0,
                        last: response.data.pageSize * response.data.pageNumber + response.data.businessUnits.length,
                    });
                    setListLoading(false);
                    response.data.totalCount == 0 ? setNoSearchResult(true) : setNoSearchResult(false);
                })
                .catch(function (error) {
                    console.log(error);
                    setListLoading(false);
                });
        }
    }, [searchText, BUSearchPayload]);

    const handleSearch = (props: ListSearchChangeProps) => {
        setSearchText(props.q);
        if(props.q.length > 1) {
            setBUSearchPayload({...BUSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(props.q)})
            setListLoading(true);
        } else {
            setNoSearchResult(false);
            setBUListOnActions();
        }
    };

    const theme = createInternalTheme({ darkMode: false, theme: {} });

    return (
        <React.StrictMode>
            <ThemeProvider theme={theme}>
            <LayoutRoot className='jv-bu-tab-panel-wrapper'>
            <LayoutRootSection top bottom transparent className='jv-tab-panel-wrapper'>
                <Button key="contained" variant="contained" className={'ods-header-button add-bu-button'} onClick={handleAddBusinessDrawerOpen} color="primary">{ODS_ADD_BUSINESS_BUTTON}</Button>
                <PageHeader className={'bu-header-text'} title={ODS_TAB_BUSINESS_UNITS} variant="subHeader"/>
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    {(searchText.length <= 1 && <Breadcrumb variant="primaryLink" className='ods-breadcrumb bu-breadcrumb' maxItems={5} itemsBeforeCollapse={2} itemsAfterCollapse={2}>
                        {BUHierarchyTree.map((business, i) => (i === BUHierarchyTree.length - 1) ? (
                            <BreadcrumbItem key={i} variant label={business.name} className={'bu-breadcrumb-link'+i} />
                            ) : (<BreadcrumbItem key={i} className={'bu-breadcrumb-link'+i} onClick={() => onBreadcrumChange(business)} variant label={business.name} />))}
                    </Breadcrumb>)}
                    {(searchText.length > 1 && <Breadcrumb variant="primaryLink" className='ods-breadcrumb bu-breadcrumb'>
                        <BreadcrumbItem variant label={ODS_ALL} className='bu-breadcrumb-all' onClick={() => {onBreadcrumChange({name:ODS_ALL, eid:''}); setSearchText('')}} />
                        <BreadcrumbItem variant maxLength={60} className='bu-breadcrumb-search-all' label={'Search Results for: "'+searchText+'"'} />
                    </Breadcrumb>)}
                </Paper>
                {(showInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
                <ListBase
                    className={'bu-list-wrapper'}
                    showSearch
                    labelSearch={ODS_SEARCH_PLACEHOLDER}
                    labelSearchAria={ODS_SEARCH_BUSINESS_UNITS}
                    // @ts-ignore
                    results={results}
                    onSearch={handleSearch}
                    appliedSearchQuery={searchText}
                    pagingContext={pagingContext}
                    onPageChanged={onPageChanged}
                    onPaginationChange={(event: SelectChangeEvent) => onPaginationChange(event)}
                    rowsPerPageLabel={ODS_ROWS_PER_PAGE_LABEL}
                    resultsPerPage={perPageSize}
                    perPageOptions={OrgElementsContext.perPageSelectOptions}
                            viewContexts={[
                                {
                                    ariaLabel: 'Datatable View',
                                    icon: <TableChartIcon />,
                                    renderResults: (viewContextProps) => {
                                        if (listLoading) {
                                            return (<div className={'bu-list-loader'} style={{ display: 'flex', justifyContent: 'center' }}>
                                                <CircularProgress size={34} />
                                            </div>)
                                        } else {
                                            if(!noSearchResult){
                                                return (<DataTable className={'bu-list-data-table'}
                                                    {...viewContextProps}
                                                    getRowID={node => node.id}
                                                    columns={[
                                                        { id: 'BusinessUnits', columnName: ODS_TAB_BUSINESS_UNITS, maxWidth:250, renderCell: (node: IBusinessData) => populateTitle(node)},
                                                        { id: 'SubBusinessUnits', columnName: ODS_SUB_BUSINESS_UNITS, renderCell: (node: IBusinessData) => <Typography variant="body2" disabled={!node.active}>{node.subBusinessUnitsCount}</Typography>, maxWidth: 80 },
                                                        { id: 'Description', columnName: ODS_DESCRIPTION, renderCell: (node: IBusinessData) => <Tooltip arrow title={node.description} placement="bottom"><span><Typography className='jv-business-desc' variant="body2" disabled={!node.active}>{node.description}</Typography></span></Tooltip>, maxWidth: 250 },
                                                        ...(searchText.length > 1 ? [{ id: 'ParentBusinessUnit', columnName: ODS_PARENT_BUSINESS_UNIT, renderCell: (node: IBusinessData) => renderParentBu(node), maxWidth: 160}] : []),
                                                        { id: 'Status', columnName: ODS_STATUS, renderCell: (node: IBusinessData) => (node.active) ? <Typography variant="body2" color="success" sx={{ color: 'green' }}>Active</Typography> : <Typography variant="body2" color="error" className='error-text-style' disabled>Inactive</Typography>, maxWidth: 48 },
                                                    ]}
                                                    renderActions={(node: IBusinessData) => {
                                                        return (<ActionGroup id="primary-buttons-actions" className={'bu-list-action-links'} actions={transformActionList(node.actions)} data={node} />)
                                                    }}
                                                    labelCheckboxAriaLabel="Select candidate with name {label}"
                                                    labelCheckboxKey="name"
                                                />)
                                            } else {
                                                return (<div className={'bu-list-no-results'}><Paper sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}> {I18N_NO_RESULTS_FOUND}</Paper></div>)
                                            }
                                        }
                                    }
                                }
                            ]}
                        />
                </LayoutRootSection>
                </LayoutRoot>
                <AddBusinessUnit
                    addDrawerState={openAddBusinessDrawer}
                    setaddDrawerState={setOpenAddBusinessDrawer}
                    selectedBU={selectedBU}
                    editSelectedBU={editSelectedBU}
                    BUHierarchyTree={BUHierarchyTree}
                    duplicateSelectedBU={duplicateSelectedBU}
                    setDuplicateSelectedBU={setDuplicateSelectedBU}
                    makeBusinessUnitActive={makeBusinessUnitActive}
                    makeBusinessUnitInActive={makeBusinessUnitInActive}
                />
            </ThemeProvider>
            
        </React.StrictMode>
    );
}

export default BusinessUnit;
